import type { BoxProps } from '@hover/blueprint';
import { Box } from '@hover/blueprint';
import React, { useEffect, useState } from 'react';

import { useRenderingContext } from 'src/modules/renderings/hooks';
import { Page, swipe } from 'src/shared/telemetry';

const filledEllispeStyling = {
  width: '9px',
  height: '9px',
  backgroundColor: 'neutral.900',
};

const emptyEllipseStyling = {
  width: '7px',
  height: '7px',
  backgroundColor: 'neutral.300',
};

const Ellipse = (props: BoxProps) => (
  <Box
    borderRadius="50%"
    display="inline-block"
    transitionDuration="350ms"
    transitionProperty="background-color"
    {...props}
  />
);

type PaginationDotsProps = {
  totalItems: number;
  scrollPosition: number;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
};

const getCurrentPosition = (
  currentPosition: number,
  totalWidth: number,
  totalItems: number,
) => Math.round((currentPosition / totalWidth) * totalItems);

const PaginationDots = ({
  totalItems,
  scrollPosition,
  scrollContainerRef,
}: PaginationDotsProps) => {
  const [previousPosition, setPreviousPosition] = useState(0);
  const { trackingData } = useRenderingContext();
  const currentPosition = getCurrentPosition(
    scrollPosition,
    scrollContainerRef?.current?.scrollWidth || 1,
    totalItems,
  );

  useEffect(() => {
    const positionChange = currentPosition - previousPosition;
    if (positionChange) {
      const direction = positionChange >= 0 ? 'right' : 'left';
      swipe({
        ...trackingData,
        page_or_screen_name: Page.RENDERINGS_HUB,
        track_id: `rendering-product-carousel-swipe-${direction}`,
      });
      setPreviousPosition(currentPosition);
    }
  }, [currentPosition, previousPosition, setPreviousPosition, trackingData]);

  const dots = Array(totalItems)
    .fill('')
    .map((_, index) => (
      <Ellipse
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        sx={
          currentPosition === index ? filledEllispeStyling : emptyEllipseStyling
        }
      />
    ));

  return (
    <Box
      alignItems="center"
      bottom="-24px"
      display={{ base: 'flex', tablet: 'none' }}
      gap="3px"
      left="50%"
      padding={200}
      position="absolute"
      transform="translate(-50%, 0)"
      transition="all 350ms ease-in-out"
    >
      {dots}
    </Box>
  );
};

export { PaginationDots };
